import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppSelector } from "../../../redux/hooks";
import { formatDatamartFilters } from "../../../utils/formatV2DatamartFilters";
import { apiClient } from "../../../zodios/client";
import { type SQPReport, SQPR_SEARCH_TERM_METRICS } from "../../../zodios/apiDefinitions/api/sqpReports";
import { shapeSQPRows } from "../sqprMetrics";

const REPORTS_KEY = "sqpreport";

const searchTermMetrics = SQPR_SEARCH_TERM_METRICS.join(",");

/** The uploaded reports for the current marketplace. */
export const useSQPReports = () => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  return useQuery({
    queryKey: [REPORTS_KEY, marketPlace],
    queryFn: async () => (await apiClient.getSQPReports({ queries: { marketplace: marketPlace } })).results,
  });
};

/** Refetches the report list, after an upload or a delete. */
export const useInvalidateSQPReports = () => {
  const queryClient = useQueryClient();
  return async () => await queryClient.invalidateQueries({ queryKey: [REPORTS_KEY] });
};

export const useDeleteSQPReports = () => {
  const invalidate = useInvalidateSQPReports();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map(async (id) => await apiClient.deleteSQPReport(undefined, { params: { id } })));
    },
    onSuccess: invalidate,
  });
};

/** One report's rows, joined with the ads stats for the same queries and dates. `undefined` while there is no report. */
export const useSQPReportRows = (report: SQPReport | undefined) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const rowsQuery = useQuery({
    queryKey: ["sqpreportrows", report?.id],
    queryFn: async () => (await apiClient.getSQPReportRows({ queries: { report: report!.id } })).results,
    enabled: !!report,
  });

  const filters = useMemo(
    () =>
      report &&
      formatDatamartFilters({
        marketplace: marketPlace,
        date__gte: report.start_date,
        date__lte: report.end_date,
        sqp_report_id: report.id,
      }),
    [marketPlace, report],
  );

  const statsQuery = useQuery({
    queryKey: ["sqprSearchTermStats", filters],
    queryFn: async () =>
      await apiClient.getSQPRSearchTermStats({
        queries: { dimensions: "query", metrics: searchTermMetrics, filters: filters! },
      }),
    enabled: !!filters,
  });

  // Wait for the ads stats too, so the rows are shaped (and handed to the table) once rather than twice.
  const isLoading = rowsQuery.isLoading || statsQuery.isLoading;
  const data = useMemo(
    () => (report && rowsQuery.data && !isLoading ? shapeSQPRows(rowsQuery.data, statsQuery.data) : undefined),
    [report, rowsQuery.data, statsQuery.data, isLoading],
  );

  return { data, isLoading };
};
